import {
  ChangeDetectionStrategy,
  Component,
  input,
  computed,
  WritableSignal,
  signal,
  forwardRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonModal, IonPicker, IonPickerColumn, IonPickerColumnOption, IonToolbar, IonButtons } from "@ionic/angular/standalone";
import { ButtonComponent } from '../button/button';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'lib-time-picker',
  imports: [ IonButtons, IonToolbar, IonPicker, IonPickerColumn, IonPickerColumnOption, IonModal, CommonModule, ButtonComponent ],
  templateUrl: './time-picker.html',
  styleUrl: './time-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePicker),
      multi: true
    }]
})
export class TimePicker implements ControlValueAccessor{


  @ViewChild(IonModal) modal!: IonModal;
  @ViewChild('inlineInput') inlineInput!: any;
  
  minutesSeconds = input(false)
  maxHours = input(24)
  
  value: WritableSignal<number> = signal(0);

  pickerOpen = false;
  
  disabled = false;
  isEditingInline = signal(false);

  // signals que contenen el valor real de cada columna del component
  hours: WritableSignal<number> = signal(0);
  minutes: WritableSignal<number> = signal(0);
  
  // signals que contenen el valor temporal (mentres s'edita el picker) de cada columna
  tmpHours: WritableSignal<number> = signal(0);
  tmpMinutes: WritableSignal<number> = signal(0);

  // string de les hores que es mostra al DOM
  hoursView = computed(() => {
    if(this.hours() === 0 && this.minutes() === 0){
      return "--";
    }
    return String(this.hours()).padStart(2, '0');
  })

  // string dels minutes que es mostren al DOM
  minutesView = computed(() => {
    if(this.hours() === 0 && this.minutes() === 0){
      return "--";
    }
    return String(this.minutes()).padStart(2, '0');
  })

  // valors de les hores i dels minuts per mostrar com a opcions a les columnes
  hoursOptions = this.generateHourOptions();
  minutesOptions = this.generateMinuteOptions();


  togglePickerModal(){
    if(this.disabled) return;
    const isNative = Capacitor.isNativePlatform(); 
    //? si esta en desktop mode edicio inline, si mobil obre el picker modal
    if (!isNative) {
      this.isEditingInline.set(true);
      setTimeout(() => {
        if (this.inlineInput) {
          const input = this.inlineInput.nativeElement;
          input.value = this.hours() === 0 && this.minutes() === 0 ? '' : this.hoursView() + ':' + this.minutesView();
          input.focus();
          input.select();
        }
      }, 0);
      return;
    }

    this.pickerOpen = true;
  }

  onInputBlur() {
    if (this.inlineInput) {
      const input = this.inlineInput.nativeElement;
      const value = input.value.replace(/\D/g, '');
      let h = 0;
      let m = 0;

      if (value.length > 0) {
        h = parseInt(value.slice(0, 2) || '0');
        m = parseInt(value.slice(2, 4) || '0');
        
        //? si les hores superen el maxim, s'ajusta a 24
        if (h > this.maxHours()) {
          h = this.maxHours();
          m = 0;
        }

        //@ si l'usuari posa 24, forcem minuts a 0 
        if (h === 24) {
          m = 0;
        } else {
          m = Math.min(m, 59);
        }
      }

      //! Només actualitzem si el valor ha canviat realment respecte al que teníem
      if (h !== this.hours() || m !== this.minutes()) {
        this.hours.set(h);
        this.minutes.set(m);
        this.updateOutputValue();
      }
    }
    this.isEditingInline.set(false);
  }
  //! controls input edició inline
  onKeyDown(event: KeyboardEvent) {
    //! enter: confirmar 
    // ! escape: cancelar 
    if (event.key === 'Enter') {
      this.onInputBlur();
    } else if (event.key === 'Escape') {
      this.isEditingInline.set(false);
    }
  }

  onInputChange(event: any) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Només números
    
    //! mask, afegeix : quan s'escriuen hores
    if (value.length > 4) value = value.slice(0, 4);
    
    let formattedValue = '';
    if (value.length > 0) {
      if (value.length <= 2) {
        formattedValue = value;
      } else {
        formattedValue = value.slice(0, 2) + ':' + value.slice(2, 4);
      }
    }
    
    input.value = formattedValue;

    //@ actualitza el valor si tenim HH:MM complet
    if (value.length === 4) {
      let h = parseInt(value.slice(0, 2) || '0');
      let m = parseInt(value.slice(2, 4) || '0');
      
      //@ validacio auto
      if (h > this.maxHours()) {
        h = this.maxHours();
        m = 0;
      }

      if (this.maxHours() === 24 && h === 24) {
        m = 0;
      } else {
        m = Math.min(m, 59);
      }

      this.hours.set(h);
      this.minutes.set(m);
      
      this.updateOutputValue();
    }
  }

  private updateOutputValue() {
    if (this.minutesSeconds()){
      this.onChange(Number(this.hours())*3600 + Number(this.minutes())* 60)
    } else {
      this.onChange(Number(this.hours())*60 + Number(this.minutes()))
    }
  }
  
  onWillDismiss(event: CustomEvent){

    this.pickerOpen = false;

    if (event.detail.role === 'confirm') {
          const { hours, minutes } = event.detail.data;
          if (hours !== this.hours() || minutes !== this.minutes()) {
            this.hours.set(hours);
            this.minutes.set(minutes);
            this.updateOutputValue();
          }      
        } else {
          this.tmpHours.set(this.hours());
          this.tmpMinutes.set(this.minutes());
        }
      }
  
  onIonPickerHoursColChange(event: CustomEvent){
    const h = event.detail.value;
    this.tmpHours.set(h);
    if (h >= this.maxHours()) this.tmpMinutes.set(0);
  }

  onIonPickerMinutesColChange(event: CustomEvent){
    if (this.tmpHours() >= this.maxHours()) return;
    this.tmpMinutes.set(event.detail.value);
  }

  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number): void {
    this.value.set(value ?? 0);
    if(this.value() > 0){
      // Si minutesSeconds = true, el valor arriba en segons
      // Si minutesSeconds = false, el valor arriba en minuts
      this.hours.set(Math.floor(this.value() / 60));
      this.minutes.set(this.value() % 60);
      this.tmpHours.set(this.hours());
      this.tmpMinutes.set(this.minutes());
    }else{
      this.hours.set(0);
      this.minutes.set(0);
      this.tmpHours.set(this.hours());
      this.tmpMinutes.set(this.minutes());
    }

    this.onChange(value);
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  
  formatPickerLabel(value: number ){
    return String(value).padStart(2, '0');
  }
  

  generateHourOptions() {
    const options = [];
    for (let i = 0; i <= this.maxHours(); i++) {
      const value = i < 10 ? `0${i}` : `${i}`;
      options.push(i);
    }
    return options;
  }

  generateMinuteOptions() {
    const options = [];
    for (let i = 0; i < 60; i++) {
      const value = i < 10 ? `0${i}` : `${i}`;
      options.push(i);
    }
    return options;
  }



  onReset() {
    this.modal.dismiss(null, 'cancel');
  }


  onConfirm() {
    this.modal.dismiss(
      { hours: this.tmpHours(), minutes: this.tmpMinutes() },
      'confirm'
    );
  }


}